import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { ticketsAPI } from '../../services/api';
import { LifeBuoy, Loader2, MessageSquare, ChevronRight, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const STATUS_COLORS = {
  open: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  answered: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  closed: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
};
const STATUS_LABELS = { open: 'Offen', answered: 'Beantwortet', closed: 'Geschlossen' };
const PRIORITY_LABELS = { low: 'Niedrig', normal: 'Normal', high: 'Hoch' };
const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const SupportPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const fetchTickets = async () => {
    try {
      const res = await ticketsAPI.list();
      setTickets(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchDetail = async (id) => {
    try {
      const res = await ticketsAPI.get(id);
      setSelectedTicket(res.data);
    } catch (err) { console.error(err); }
  };

  const handleReply = async () => {
    if (!reply.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await ticketsAPI.reply(selectedTicket.id, reply);
      setReply('');
      fetchDetail(selectedTicket.id);
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  useEffect(() => { fetchTickets(); }, []);

  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-6 h-6 text-[#EC2229] animate-spin" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="support-page">
        <div className="flex items-center gap-3 mb-6">
          <LifeBuoy className="w-7 h-7 text-[#EC2229]" />
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">Support-Tickets</h1>
            <p className="text-slate-400 text-sm">Ihre Supportanfragen zu hochgeladenen Dateien</p>
          </div>
        </div>

        {selectedTicket ? (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => { setSelectedTicket(null); fetchTickets(); }} className="text-slate-400 hover:text-white" data-testid="back-to-list">&larr; Zurück</Button>
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-heading font-semibold text-white text-lg">{selectedTicket.subject}</h2>
                  <p className="text-xs text-slate-500">{selectedTicket.ticket_number} | File: {selectedTicket.file_ticket_id}</p>
                </div>
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border ${STATUS_COLORS[selectedTicket.status] || STATUS_COLORS.open}`}>
                  {STATUS_LABELS[selectedTicket.status] || selectedTicket.status}
                </span>
              </div>

              <div className="space-y-3 max-h-[28rem] overflow-y-auto mb-4 pr-1">
                {selectedTicket.messages?.map(msg => (
                  <div key={msg.id} className={`p-3 rounded-lg ${msg.is_staff ? 'bg-[#EC2229]/10 border border-[#EC2229]/20 ml-8' : 'bg-white/[0.03] border border-white/5 mr-8'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${msg.is_staff ? 'text-[#EC2229]' : 'text-white'}`}>{msg.user_name} {msg.is_staff ? '(Staff)' : ''}</span>
                      <span className="text-xs text-slate-500">{formatDate(msg.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">{msg.message}</p>
                  </div>
                ))}
              </div>

              {selectedTicket.status !== 'closed' && (
                <div className="flex gap-2">
                  <Input value={reply} onChange={e => setReply(e.target.value)} placeholder="Antwort schreiben..." className="input-field flex-1" data-testid="reply-input" onKeyDown={e => e.key === 'Enter' && handleReply()} />
                  <Button onClick={handleReply} disabled={sending || !reply.trim()} className="btn-primary" data-testid="send-reply">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <MessageSquare className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">Keine Support-Tickets vorhanden</p>
                <p className="text-xs text-slate-500 mt-1">Sie können Tickets über die Datei-Detailseite erstellen</p>
              </div>
            ) : (
              tickets.map(t => (
                <div key={t.id} onClick={() => fetchDetail(t.id)} className="glass-card p-4 flex items-center justify-between cursor-pointer hover:border-white/20 transition-all" data-testid={`ticket-${t.id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-[#EC2229]/10 flex items-center justify-center flex-shrink-0">
                      <LifeBuoy className="w-5 h-5 text-[#EC2229]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{t.subject}</p>
                      <p className="text-xs text-slate-500">{t.ticket_number} | File: {t.file_ticket_id} | {formatDate(t.updated_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${t.priority === 'high' ? 'text-red-400 bg-red-500/10' : 'text-slate-400'}`}>{PRIORITY_LABELS[t.priority]}</span>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold border ${STATUS_COLORS[t.status] || STATUS_COLORS.open}`}>
                      {STATUS_LABELS[t.status] || t.status}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SupportPage;
